import styled from 'styled-components'
import { Spacings, Text, Devices } from '@styles'
import { NavigationWrapper } from '@base'
import { Button, Card } from '@baseV2'
import Table from '@baseV2/composite/table'

export { Button, Pill, Card } from '@baseV2'

const statusOf = (theme, color) => theme.colors.status[color] || theme.colors.status.neutral

export const FiltersWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_2B};

	@media ${Devices.tablet} {
		padding: 0 0 ${Spacings.SPACING_2B};
	}
`

// same pill as the status filters on the active enquiries page — the selected one fills with its dot colour
export const FilterPill = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_6};
	padding: ${Spacings.SPACING_6} ${Spacings.SPACING_3B};
	border-radius: ${Spacings.SPACING_5B};
	border: 1px solid ${({ theme, $active }) => ($active ? 'transparent' : theme.palette.background.tablecolumn)};
	background: ${({ theme, $active, $dotColor }) => ($active ? $dotColor : theme.palette.background.white)};
	color: ${({ theme, $active }) => ($active ? theme.palette.text.white : 'inherit')};
	font-weight: ${({ $active }) => ($active ? Text.BOLD_600 : Text.BOLD_400)};
	cursor: pointer;
	font-size: ${Text.MEDIUM};
	white-space: nowrap;
	user-select: none;
	transition: background 0.15s;

	&:hover {
		background: ${({ theme, $active, $dotColor }) => ($active ? $dotColor : theme.palette.background.tablegrey)};
	}
`

export const FilterDot = styled.span`
	display: inline-block;
	width: ${Spacings.SPACING_2B};
	height: ${Spacings.SPACING_2B};
	border-radius: 50%;
	background: ${({ $color }) => $color};
	flex-shrink: 0;
`

export const ApprovalNavigationWrapper = styled(NavigationWrapper)`
	display: flex;
	align-items: center;
`

export const SearchButtonWrapper = styled.div`
	margin-left: auto;
	flex-shrink: 0;
`

export const MetricsTable = styled(Table)`
	background: ${({ theme }) => theme.colors.surface.card};

	// each row holds a full set of approval cards, so the separator needs more weight
	tbody td {
		border-bottom: 2px solid ${({ theme }) => theme.colors.border.strong};
	}
`

export const RowWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
	width: 100%;

	@media ${Devices.tablet} {
		flex-direction: row;
		align-items: stretch;
	}
`

export const EnquiryRow = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: ${Spacings.SPACING_4B};
	width: 100%;

	@media ${Devices.tablet} {
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
	}
`

export const EnquiryPanel = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	min-width: 0;
`

export const CountBadge = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: ${Spacings.SPACING_5B};
	height: ${Spacings.SPACING_5B};
	padding: 0 ${Spacings.SPACING_1B};
	border-radius: ${({ theme }) => theme.radius.full};
	background: ${({ theme, $color = 'success' }) => (theme.colors.status[$color] || theme.colors.status.neutral).icon};
	color: ${({ theme }) => theme.colors.text.inverse};
	font-size: ${Text.EXTRA_SMALL};
	font-weight: ${Text.BOLD_700};
	line-height: 1;
`

export const ReasonPanel = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	flex: 1;
	min-width: 0;
`

export const LinkText = styled.a`
	display: inline-flex;
	align-items: center;
	gap: ${Spacings.SPACING_1B};
	width: fit-content;
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_700};
	color: ${({ theme }) => theme.colors.brand.primary};
	text-decoration: none;
	cursor: pointer;
	transition: opacity 0.15s ease;

	&:hover {
		opacity: 0.85;
		text-decoration: underline;
	}

	svg {
		font-size: ${Text.LARGE};
	}
`

export const Title = styled.div`
	font-size: ${({ $size }) => ($size === 'lg' ? Text.LARGE : Text.MEDIUM)};
	font-weight: ${Text.BOLD_700};
	color: ${({ theme }) => theme.colors.text.primary};
	line-height: ${Text.LINE_HEIGHT_MIN};
`

export const MetaText = styled.div`
	font-size: ${Text.MEDIUM};
	color: ${({ theme }) => theme.colors.text.secondary};
	padding-left: ${Spacings.SPACING_3B};
`

export const Chip = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${Spacings.SPACING_1B};
	width: fit-content;
	padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_3B};
	border-radius: ${({ theme, $square }) => ($square ? theme.radius.sm : theme.radius.full)};
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_600};
	white-space: nowrap;
	cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

	background: ${({ theme, $color = 'info', $variant }) => ($variant === 'outline' ? 'transparent' : statusOf(theme, $color).bg)};
	color: ${({ theme, $color = 'info' }) => statusOf(theme, $color).text};
	border: 1px solid ${({ theme, $color = 'info', $variant }) => ($variant === 'outline' ? statusOf(theme, $color).border : 'transparent')};

	svg {
		font-size: 1em;
		cursor: pointer;
	}
`

export const ChipIconButton = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: ${({ theme }) => theme.colors.brand.primary};
	cursor: pointer;
	transition: opacity 0.15s ease;

	&:hover {
		opacity: 0.85;
	}

	svg {
		font-size: ${Text.LARGE};
	}
`

export const ChipRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ $gap }) => $gap || Spacings.SPACING_3B};
`

export const ChipLine = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: ${Spacings.SPACING_3B};
`

export const KeyValue = styled.span`
	display: inline-flex;
	align-items: ${({ $bordered }) => ($bordered ? 'center' : 'baseline')};
	gap: ${Spacings.SPACING_1B};
	font-size: ${Text.SMALL};
	white-space: nowrap;
	width: fit-content;
	padding: ${({ $bordered }) => ($bordered ? `${Spacings.SPACING_1B} ${Spacings.SPACING_3B}` : '0')};
	border: ${({ theme, $bordered }) => ($bordered ? `1px solid ${statusOf(theme, 'info').border}` : 'none')};
	border-radius: ${({ theme, $bordered }) => ($bordered ? theme.radius.full : '0')};
`

export const KeyLabel = styled.span`
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
	font-weight: ${Text.BOLD_500};
`

export const KeyVal = styled.span`
	color: ${({ theme, $color }) => ($color ? statusOf(theme, $color).text : theme.colors.text.primary)};
	font-weight: ${Text.BOLD_700};
`

export const MetricCard = styled(Card)`
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
`

export const CardTopRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_4B};
`

export const ApproversFooter = styled.div`
	padding-top: ${Spacings.SPACING_3B};
	border-top: 1px solid ${({ theme }) => theme.colors.border.default};
`

export const MetricMain = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2B};
	flex: 1;
	min-width: 0;
`

export const CardHeader = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_1B};
`

export const ApproversRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${Spacings.SPACING_1B};
`

export const StatusCell = styled.div`
	display: flex;
	flex-direction: column;
	align-items: ${({ $top }) => ($top ? 'flex-end' : 'flex-start')};
	justify-content: center;
	align-self: ${({ $top }) => ($top ? 'flex-start' : 'auto')};
	gap: ${Spacings.SPACING_1B};
	flex: 0 0 auto;

	@media ${Devices.tablet} {
		flex: 0 0 9rem;
	}
`

export const ActionsCell = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: ${Spacings.SPACING_2B};
	flex: 0 0 auto;

	@media ${Devices.tablet} {
		flex: 0 0 5rem;
	}
`

export const Stack = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ $gap }) => $gap || Spacings.SPACING_3B};
	width: 100%;
`

export const SplitHeader = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	gap: ${Spacings.SPACING_4B};
	padding: 0 ${Spacings.SPACING_4B};
`

export const SplitHeaderMain = styled.span`
	flex: 1;
	min-width: 0;
`

export const SplitHeaderStatus = styled.span`
	flex: 0 0 auto;

	@media ${Devices.tablet} {
		flex: 0 0 9rem;
	}
`

export const SplitHeaderActions = styled.span`
	flex: 0 0 auto;
	text-align: center;

	@media ${Devices.tablet} {
		flex: 0 0 5rem;
	}
`

export const IconButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: ${Spacings.SPACING_8B};
	width: ${Spacings.SPACING_8B};
	border-radius: ${({ theme }) => theme.radius.full};
	background: transparent;
	border: none;
	padding: 0;
	color: ${({ theme, $color = 'neutral' }) => statusOf(theme, $color).icon};
	cursor: pointer;
	transition: all 0.15s ease;

	&:hover:not(:disabled) {
		background: ${({ theme, $color = 'neutral' }) => statusOf(theme, $color).bg};
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	svg {
		font-size: ${Text.X_LARGE};
	}
`

export const ButtonGroup = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${Spacings.SPACING_3B};
`

export const EnquiryActionButton = styled(Button)`
	flex: 0 0 auto;
	width: 8.5rem;
	height: ${Spacings.SPACING_10B};
	justify-content: center;
	white-space: nowrap;
`

export const DrawerSummaryCard = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	background: ${({ theme }) => theme.colors.brand.subtle};
	border: 1px solid ${({ theme }) => theme.colors.brand.muted};
	border-radius: ${({ theme }) => theme.radius.lg};
	padding: ${Spacings.SPACING_4B};
	margin-bottom: ${Spacings.SPACING_4B};
`

export const DetailCard = styled(Card)`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
	margin: ${Spacings.SPACING_4B} 0;
`

export const DetailItem = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1B};
	min-width: 0;
	grid-column: ${({ $fullRow }) => ($fullRow ? '1 / -1' : 'auto')};
`

export const DetailLabel = styled.span`
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.primary};
	font-weight: ${Text.BOLD_400};
	text-transform: uppercase;
	letter-spacing: 0.025em;
`

export const DetailValue = styled.span`
	font-size: ${Text.BASE_SMALL};
	color: ${({ theme }) => theme.colors.text.primary};
	font-weight: ${Text.BOLD_500};
	white-space: pre-line;
	overflow-wrap: anywhere;
`

// matches the elevation DetailCard gets from Card
export const DetailTable = styled(Table)`
	box-shadow: ${({ theme }) => theme.shadows.sm};
`

export const AddressSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2B};
	margin-top: ${Spacings.SPACING_4B};

	/* AddressCard does not forward className, so the elevation is applied to its root here */
	> div {
		box-shadow: ${({ theme }) => theme.shadows.sm};
	}
`

export const SummaryRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
`
